import AgencyHelper, { AgencyConstants } from 'features/Agency/agencyHelper';
import ChargeHelper, { ChargeConstants } from 'features/Charge/chargeHelper';
import React from 'react';
import ChargePointListView from 'features/Charge/ChargePoint/components/ChargePointListView';
import { AnimatePresence, motion } from 'framer-motion';
import AppTabs from 'general/components/AppTabs';
import KTLabel, { KTLabelSize } from 'general/components/OtherKeenComponents/KTLabel';
import CustomDateRangePicker, {
  dateFilter,
  getDateFilterOption,
} from 'general/components/Others/CustomDateRangePicker';
import DashboardChartCard from 'general/components/quybka/Cards/DashboardChartCard';
import BarChart from 'general/components/quybka/Charts/BarChart';
import AppColors from 'general/constants/AppColors';
import AppConfigs from 'general/constants/AppConfigs';
import AppResource from 'general/constants/AppResource';
import UIHelper from 'general/helpers/UIHelper';
import Utils from 'general/utils/Utils';
import useRouter from 'hooks/useRouter';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { ReactBingmaps } from 'react-bingmaps';
import { useSelector } from 'react-redux';
import ShowMoreText from 'react-show-more-text';
import ChargeStationEnergyAccountListView from '../ChargeStationEnergyAccountListView';
import ChargeStationLogView from '../ChargeStationLogView';
import DealerHelper from 'features/ManageDealer/dealerHelper';
import ChargeInventoryListView from 'features/Charge/ChargeInventory/components/ChargeInventoryListView';
import useAppConfig from 'hooks/useAppConfig';
import ChargeStationAcceptanceListView from '../ChargeStationAcceptanceListView';
import AppConstants from 'general/constants/AppConstants';
import MaintainerTicketListView from 'features/Maintainer/MaintainerTicket/components/MaintainerTicketListView';

ChargeStationDetailView.propTypes = {
  chargeStationId: PropTypes.string,
};

function ChargeStationDetailView(props) {
  // MARK: ---- PROPERTIES ----
  const { chargeStationId } = props;
  const appConfig = useAppConfig();
  const configServices = useSelector((state) => state.app.appConfig?.services ?? []);
  const appConfigList = useSelector((state) => state.app.appConfig?.config ?? []);
  const router = useRouter();
  const { user, permissionList } = useSelector((state) => state.auth);
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'OPERATOR';
  const isInvestor = user?.role === 'AGENCY' && user?.agency?.primaryRole === 'INVESTOR';
  const isLandlord = user?.role === 'AGENCY' && user?.agency?.primaryRole === 'LANDLORD';
  const [chargeStationDetail, setChargeStationDetail] = useState(null);
  const evnConfig = useMemo(() => {
    const operatorId = chargeStationDetail?.chargeOperator?.operatorId;
    if (!operatorId) return null;
    const raw = _.find(appConfigList, (item) => item.key === `ELECTRICITY_PURCHASE_PRICE_${operatorId}`)?.value;
    if (!raw) return null;
    try { return JSON.parse(raw); } catch (_) { return null; }
  }, [appConfigList, chargeStationDetail]);
  const [chargePointList, setChargePointList] = useState([]);
  useEffect(() => {
    setChargePointList(chargeStationDetail?.chargePoints || []);
  }, [chargeStationDetail]);
  useEffect(() => {
    ChargeHelper.API.getChargeStationDetail(chargeStationId).then((chargeStation) => {
      if (chargeStation && _.isObject(chargeStation)) setChargeStationDetail(chargeStation);
    });
  }, [chargeStationId]);
  const [chartData, setChartData] = useState({});
  const [chartFilter, setChartFilter] = useState({
    from: getDateFilterOption(dateFilter.baMuoiNgayGanNhat)[0].format('YYYY-MM-DD'),
    to: getDateFilterOption(dateFilter.baMuoiNgayGanNhat)[1].format('YYYY-MM-DD'),
    dateFilter: dateFilter.baMuoiNgayGanNhat,
  });
  useEffect(() => {
    // lay du lieu chart
    ChargeHelper.API.Dashboard.requestGetStationStatistics({
      stationId: chargeStationId,
      from: chartFilter.from,
      to: chartFilter.to,
    }).then((res) => {
      if (_.isObject(res)) setChartData(res);
    });
  }, [chargeStationId, chartFilter]);
  const areaTree = useSelector((state) => state.app.areaTree);
  const [chargeStationArea, setChargeStationArea] = useState(null);
  const [chargeStationParentArea, setChargeStationParentArea] = useState(null);
  useEffect(() => {
    if (areaTree && !_.isEmpty(areaTree) && chargeStationDetail) {
      const chargeStationAreaId = chargeStationDetail?.areaId;
      const chargeStationArea = ChargeHelper.Utils.getAreaById([areaTree], chargeStationAreaId);
      setChargeStationArea(chargeStationArea);
      setChargeStationParentArea(
        ChargeHelper.Utils.getAreaById([areaTree], chargeStationArea?.parentId)
      );
    }
  }, [areaTree, chargeStationDetail]);
  const chargeStationMapEl = useMemo(() => {
    if (chargeStationDetail) {
      return (
        <ReactBingmaps
          key={`charge_station_map_key_${chargeStationDetail?.stationId}`}
          id={`charge_station_map_id_${chargeStationDetail?.stationId}`}
          mapOptions={AppConfigs.bingMapOption}
          bingmapKey={process.env.REACT_APP_BING_MAP_API_KEY}
          center={[chargeStationDetail?.latitude, chargeStationDetail?.longitude]}
          zoom={15}
          mapTypeId="grayscale"
          className="customClass"
          infoboxesWithPushPins={
            Utils.isValid(chargeStationDetail?.latitude) &&
              Utils.isValid(chargeStationDetail?.longitude)
              ? [
                {
                  location: [chargeStationDetail?.latitude, chargeStationDetail?.longitude],
                  infoboxOption: {
                    htmlContent: ' ',
                    showCloseButton: false,
                    showPointer: false,
                  },
                  pushPinOption: {
                    icon: AppResource.icons.pin.normal,
                  },
                },
              ]
              : []
          }
        />
      );
    }
    return <></>;
  }, [chargeStationDetail]);
  const { labels, data } = Utils.generateRandomChartData(7, 0, 400);
  const [chargeStationInvestor, setChargeStationInvestor] = useState(null);
  const [chargeStationLandlord, setChargeStationLandlord] = useState(null);
  useEffect(() => {
    if (chargeStationDetail?.agencyId) {
      AgencyHelper.API.requestAgencyDetail(chargeStationDetail?.agencyId).then((agency) => {
        if (agency && _.isObject(agency)) {
          setChargeStationInvestor(agency);
        }
      });
    }
    if (chargeStationDetail?.landlordId) {
      AgencyHelper.API.requestAgencyDetail(chargeStationDetail?.landlordId).then((agency) => {
        if (agency && _.isObject(agency)) {
          setChargeStationLandlord(agency);
        }
      });
    }
  }, [chargeStationDetail]);
  const tabs = useMemo(() => {
    return ['Danh sách trụ sạc', 'Tài khoản điện lực', 'Thiết bị khác'/*, 'Lịch sử bảo trì/sửa chữa'*/].reverse();
  }, []);
  const [selectedTab, setSelectedTab] = useState(tabs[2]);

  // dien sac theo ngay
  const chartEnergyByDateEl = useMemo(() => {
    const energyByDate = chartData?.dailyEnergy?.dailyEnergy ?? [];
    let labels = energyByDate.map((item) => Utils.formatDateTimeToString(item.date, 'DD'));
    let data = energyByDate.map((item) => ((item.energy ?? 0) / 1000).toFixed(3));

    if (energyByDate.length > 31) {
      const groupedData = _.groupBy(energyByDate, (item) => moment(item.date).format('MM/YYYY'));
      labels = Object.keys(groupedData);
      data = labels.map((label) => {
        const total = groupedData[label].reduce((sum, item) => sum + item.energy, 0);
        return (total / 1000).toFixed(3); // Chuyển đổi sang kWh
      });
    }

    return (
      <DashboardChartCard
        title="Thống kê điện sạc"
        name="Tổng"
        value={
          Utils.formatNumber(data.reduce((sum, item) => _.toNumber(sum) + _.toNumber(item), 0)) +
          ' kWh'
        }
        valueColor="rgba(41, 132, 238, 1)"
        actionEl={
          <CustomDateRangePicker
            alignRight={true}
            toggleFontWeight="600"
            toggleElementBorderRadius={10}
            initialValue={chartFilter.dateFilter}
            onPressSave={(selectedValue, startDate, endDate) => {
              setChartFilter({
                from: startDate,
                to: endDate,
                dateFilter: selectedValue,
              });
            }}
          />
        }
        chartEl={
          <BarChart
            title="Điện sạc"
            unit="kWh"
            labels={labels}
            data={data}
            color="rgba(41, 132, 238, 0.4)"
            hoverColor="rgba(41, 132, 238, 1)"
          />
        }
      />
    );
  }, [chartData, chartFilter]);
  // doanh thu theo ngay
  const chartRevenueByDateEl = useMemo(() => {
    const revenueByDate = chartData?.dailyRevenue?.dailyRevenue ?? [];
    let labels = revenueByDate.map((item) => Utils.formatDateTimeToString(item.date, 'DD'));
    let data = revenueByDate.map((item) => (item.revenue ?? 0) / 1000000);

    if (revenueByDate.length > 31) {
      const groupedData = _.groupBy(revenueByDate, (item) => moment(item.date).format('MM/YYYY'));
      labels = Object.keys(groupedData);
      data = labels.map((label) => {
        const total = groupedData[label].reduce((sum, item) => sum + item.revenue, 0);
        return (total / 1000000).toFixed(2); // Chuyển đổi sang triệu
      });
    }

    return (
      <DashboardChartCard
        title="Thống kê doanh thu"
        name="Tổng"
        value={
          Utils.formatNumber(data.reduce((sum, item) => _.toNumber(sum) + _.toNumber(item), 0)) +
          ' triệu'
        }
        valueColor="rgba(48, 189, 111, 1)"
        actionEl={
          <CustomDateRangePicker
            alignRight={true}
            toggleFontWeight="600"
            toggleElementBorderRadius={10}
            initialValue={chartFilter.dateFilter}
            onPressSave={(selectedValue, startDate, endDate) => {
              setChartFilter({
                from: startDate,
                to: endDate,
                dateFilter: selectedValue,
              });
            }}
          />
        }
        chartEl={
          <BarChart
            title="Doanh thu"
            unit="triệu"
            labels={labels}
            data={data}
            color="rgba(48, 189, 111, 0.4)"
            hoverColor="rgba(48, 189, 111, 1)"
            showLine={true}
          />
        }
      />
    );
  }, [chartData, chartFilter]);
  // phien sac theo ngay
  const chartTransactionByDateEl = useMemo(() => {
    const transactionByDate = chartData?.dailyTransaction?.dailyTransaction ?? [];
    let labels = transactionByDate.map((item) => Utils.formatDateTimeToString(item.date, 'DD'));
    let data = transactionByDate.map((item) => item.transactions ?? 0);

    if (transactionByDate.length > 31) {
      const groupedData = _.groupBy(transactionByDate, (item) =>
        moment(item.date).format('MM/YYYY')
      );
      labels = Object.keys(groupedData);
      data = labels.map((label) => {
        const total = groupedData[label].reduce((sum, item) => sum + item.transactions, 0);
        return total;
      });
    }

    return (
      <DashboardChartCard
        title="Thống kê phiên sạc"
        name="Tổng"
        value={
          Utils.formatNumber(data.reduce((sum, item) => _.toNumber(sum) + _.toNumber(item), 0)) +
          ' phiên sạc'
        }
        valueColor="rgba(88, 86, 214, 1)"
        actionEl={
          <CustomDateRangePicker
            alignRight={true}
            toggleFontWeight="600"
            toggleElementBorderRadius={10}
            initialValue={chartFilter.dateFilter}
            onPressSave={(selectedValue, startDate, endDate) => {
              setChartFilter({
                from: startDate,
                to: endDate,
                dateFilter: selectedValue,
              });
            }}
          />
        }
        chartEl={
          <BarChart
            title="Phiên sạc"
            unit="phiên sạc"
            labels={labels}
            data={data}
            color="rgba(88, 86, 214, 0.4)"
            hoverColor="rgba(88, 86, 214, 1)"
          />
        }
      />
    );
  }, [chartData, chartFilter]);

  // MARK: ---- METHODS ----
  const handleSelectedTab = (newTab) => {
    if (selectedTab === newTab) return;
    setSelectedTab(newTab);
  };
  const buildMoreActions = () => {
    if (chargeStationDetail?.status === ChargeConstants.ChargeStationStatus.DELETED) {
      return [];
    }
    const actions = [];
    if (Utils.checkPermission(permissionList, AppConstants.permissionCode.chargeStationUpdate.code)) {
      actions.push({
        text: 'Chỉnh sửa',
        iconElement: <i className="fa-regular fa-edit" style={{ fontSize: '1.1rem' }} />,
        click: () => {
          ChargeHelper.Navigation.navigateToEditChargeStation(router, chargeStationId);
        },
      });
      if (
        [ChargeConstants.ChargeStationStatus.NORMAL].includes(chargeStationDetail?.status)
      )
        actions.push({
          text: 'Ngừng hoạt động',
          iconElement: <i className="fa-regular fa-stop" style={{ fontSize: '1.1rem' }} />,
          click: () => {
            ChargeHelper.UI.showDialogConfirmStopChargeStation(chargeStationDetail, () => {
              ChargeHelper.API.getChargeStationDetail(chargeStationId).then((chargeStation) => {
                if (chargeStation && _.isObject(chargeStation)) setChargeStationDetail(chargeStation);
              });
            });
          },
        });
      if (
        [
          ChargeConstants.ChargeStationStatus.STOPPED,
          ChargeConstants.ChargeStationStatus.DISCONNECTED,
          ChargeConstants.ChargeStationStatus.HIDDEN,
        ].includes(chargeStationDetail?.status)
      )
        actions.push({
          text: 'Khôi phục hoạt động',
          iconElement: <i className="fa-regular fa-play" style={{ fontSize: '1.1rem' }} />,
          click: () => {
            ChargeHelper.UI.showDialogConfirmRestoreChargeStation(chargeStationDetail, () => {
              ChargeHelper.API.getChargeStationDetail(chargeStationId).then((chargeStation) => {
                if (chargeStation && _.isObject(chargeStation)) setChargeStationDetail(chargeStation);
              });
            });
          },
        });
      if (
        [
          ChargeConstants.ChargeStationStatus.NORMAL,
          ChargeConstants.ChargeStationStatus.STOPPED,
          ChargeConstants.ChargeStationStatus.DISCONNECTED,
        ].includes(chargeStationDetail?.status)
      )
        actions.push({
          text: 'Bảo trì',
          iconElement: <i className="fa-regular fa-user-helmet-safety" style={{ fontSize: '1.1rem' }} />,
          click: () => {
            ChargeHelper.UI.showDialogConfirmMaintainChargeStation(chargeStationDetail, () => {
              ChargeHelper.API.getChargeStationDetail(chargeStationId).then((chargeStation) => {
                if (chargeStation && _.isObject(chargeStation)) setChargeStationDetail(chargeStation);
              });
            });
          },
        });
      if (
        [ChargeConstants.ChargeStationStatus.MAINTAINING].includes(chargeStationDetail?.status)
      )
        actions.push({
          text: 'Hoàn thành bảo trì',
          iconElement: <i className="fa-regular fa-clipboard-check" style={{ fontSize: '1.1rem' }} />,
          click: () => {
            ChargeHelper.UI.showDialogConfirmCompleteMaintainChargeStation(chargeStationDetail, () => {
              ChargeHelper.API.getChargeStationDetail(chargeStationId).then((chargeStation) => {
                if (chargeStation && _.isObject(chargeStation)) setChargeStationDetail(chargeStation);
              });
            });
          },
        });
      if (
        [
          ChargeConstants.ChargeStationStatus.NORMAL,
          ChargeConstants.ChargeStationStatus.MAINTAINING,
          ChargeConstants.ChargeStationStatus.STOPPED,
          ChargeConstants.ChargeStationStatus.DISCONNECTED,
        ].includes(chargeStationDetail?.status)
      )
        actions.push({
          text: 'Ẩn trạm sạc',
          iconElement: <i className="fa-regular fa-store-lock" style={{ fontSize: '1.1rem' }} />,
          click: () => {
            ChargeHelper.UI.showDialogConfirmHideChargeStation(chargeStationDetail, () => {
              ChargeHelper.API.getChargeStationDetail(chargeStationId).then((chargeStation) => {
                if (chargeStation && _.isObject(chargeStation)) setChargeStationDetail(chargeStation);
              });
            });
          },
        });
    }

    if (Utils.checkPermission(permissionList, AppConstants.permissionCode.chargeStationDelete.code)) {
      actions.push({
        text: 'Xoá trạm sạc',
        iconElement: <i className="fa-regular fa-trash-can" style={{ fontSize: '1.1rem' }} />,
        click: () => {
          ChargeHelper.UI.showDialogConfirmDeleteChargeStation(chargeStationDetail, () => {
            router.navigate(-1);
          });
        },
      });
    }
    return actions;
  };

  return (
    <div>
      {/* thong tin tram sac & hoa hong (neu co) */}
      <div className="row p-0 m-0 px-2">
        {/* thong tin tram sac */}
        <div className="col-lg-12 mb-1 px-2">
          {UIHelper.buildCard(
            <div>
              {/* tieu de */}
              <div className="d-flex align-items-start justify-content-between px-4 py-3">
                <div className="flex-fill">
                  <div className="d-flex align-items-center gap-3">
                    {/* avatar */}
                    <div className="">
                      {UIHelper.buildAvatar(
                        chargeStationDetail?.image
                          ? Utils.getFullUrl(chargeStationDetail?.image)
                          : null,
                        44,
                        AppResource.images.chargeConnectorTypes.imgChargeStationDefault
                      )}
                    </div>
                    <div className="d-flex flex-column">
                      <div className="d-flex align-items-start gap-2">
                        <span style={UIHelper.buildFontStyle('600', '18px', AppColors.gray[800])}>
                          {chargeStationDetail?.name?.toUpperCase()}
                        </span>
                        <span className="min-w-100px pt-1">
                          <KTLabel
                            text={ChargeHelper.Utils.getChargeStationStatusName(
                              chargeStationDetail?.status
                            )}
                            isInline={true}
                            size={KTLabelSize.medium}
                            additionalClassName={`label-light-${ChargeHelper.Utils.getChargeStationStatusColor(
                              chargeStationDetail?.status
                            )}`}
                          />
                        </span>
                      </div>
                      <span style={UIHelper.buildFontStyle('400', '14px', AppColors.gray[700])}>
                        Địa chỉ:{' '}
                        <span style={UIHelper.buildFontStyle('600', '14px', AppColors.gray[800])}>
                          {ChargeHelper.Utils.getChargeStationFullAddress(chargeStationDetail)}
                        </span>
                      </span>
                    </div>
                  </div>
                </div>
                <div className="d-flex align-items-center gap-3">
                  {(isAdmin || isInvestor || isLandlord) && UIHelper.buildMoreActionsButton(buildMoreActions())}
                </div>
              </div>

              <div className="row m-0 p-0 px-2">
                {/* left */}
                <div className="col-xl-6 m-0 px-2 mb-4">
                  {/* thong tin tram sac */}
                  <div>
                    {UIHelper.buildSectionTitle('Thông tin trạm sạc')}
                    {UIHelper.buildSectionCard(
                      <div>
                        {UIHelper.buildCardCellItem(
                          'Mã trạm sạc',
                          chargeStationDetail?.stationCode
                        )}
                        {UIHelper.buildCardCellItem(
                          'Nhà điều hành',
                          chargeStationDetail?.chargeOperator?.name
                        )}
                        {UIHelper.buildCardCellItem(
                          'Đối tượng quản lý',
                          Utils.isValid(chargeStationDetail?.agencyId)
                            ? Utils.isValid(chargeStationDetail?.landlordId) || chargeStationDetail?.agencyId === appConfig?.groupAgencyId || chargeStationDetail?.investor?.primaryRole === 'COINVESTOR'
                              ? 'NĐT liên kết'
                              : 'NĐT nhượng quyền'
                            : 'Nhà điều hành',
                        )}
                        {Utils.isValid(chargeStationDetail?.agencyId) && !Utils.isValid(chargeStationDetail?.landlordId) && chargeStationDetail?.agencyId !== appConfig?.groupAgencyId
                          ? UIHelper.buildCardCellItem(
                            `Tên NĐT ${chargeStationDetail?.investor?.primaryRole === 'COINVESTOR' ? 'liên kết' : 'nhượng quyền'}`,
                            chargeStationInvestor?.name ?? '',
                            () => {
                              AgencyHelper.Navigation.navigateToAgencyDetail(
                                null,
                                chargeStationInvestor
                              );
                            }
                          )
                          : Utils.isValid(chargeStationDetail?.landlordId) || chargeStationDetail?.agencyId === appConfig?.groupAgencyId
                            ? UIHelper.buildCardCellItem(
                              'Tên NĐT liên kết',
                              chargeStationLandlord?.name ?? '',
                              () => {
                                AgencyHelper.Navigation.navigateToAgencyDetail(
                                  null,
                                  chargeStationLandlord
                                );
                              }
                            )
                            : null
                        }
                        {UIHelper.buildCardCellItem(
                          'Hình ảnh trạm sạc',
                          chargeStationDetail?.additionalImages?.length
                            ? `Xem tệp đính kèm (${chargeStationDetail?.additionalImages?.length})`
                            : '',
                          () => {
                            if (chargeStationDetail?.additionalImages?.length)
                              DealerHelper.handleShowFile(chargeStationDetail?.additionalImages);
                          },
                          true
                        )}
                        {UIHelper.buildCardCellItem(
                          'Hồ sơ trạm sạc',
                          chargeStationDetail?.documents?.length
                            ? `Xem tệp đính kèm (${chargeStationDetail?.documents?.length})`
                            : '',
                          () => {
                            if (chargeStationDetail?.documents?.length)
                              DealerHelper.handleShowFile(chargeStationDetail?.documents);
                          },
                          false
                        )}
                      </div>
                    )}
                  </div>
                  <div className='mt-4'>
                    {UIHelper.buildSectionTitle('Giá điện mua vào')}
                    {UIHelper.buildSectionCard(
                      <div>
                        {UIHelper.buildCardCellItem(
                          'Loại giá điện đầu vào',
                          _.find(
                            ChargeHelper.Data.chargePricingStationModeOptions,
                            (item) => item.value === evnConfig?.inputPricingMode
                          )?.name,
                          null,
                          !![ChargeConstants.ChargePricingMode.FLAT, ChargeConstants.ChargePricingMode.HOURS, ChargeConstants.ChargePricingMode.TIERED].includes(evnConfig?.inputPricingMode)
                        )}
                        {evnConfig?.inputPricingMode === ChargeConstants.ChargePricingMode.FLAT &&
                          UIHelper.buildCardCellItem(
                            'Giá điện cố định',
                            evnConfig?.inputFlatPrice
                              ? Utils.formatNumber(evnConfig?.inputFlatPrice) + ' VNĐ/kWh'
                              : '',
                            null,
                            false
                          )}
                        {evnConfig?.inputPricingMode === ChargeConstants.ChargePricingMode.HOURS && (
                          <>
                            {UIHelper.buildCardCellItem(
                              'Giá điện giờ bình thường',
                              evnConfig?.inputNormalPrice
                                ? Utils.formatNumber(evnConfig?.inputNormalPrice) + ' VNĐ/kWh'
                                : '',
                              null,
                            )}
                            {UIHelper.buildCardCellItem(
                              'Giá điện giờ cao điểm',
                              evnConfig?.inputPeakPrice
                                ? Utils.formatNumber(evnConfig?.inputPeakPrice) + ' VNĐ/kWh'
                                : '',
                              null,
                            )}
                            {UIHelper.buildCardCellItem(
                              'Giá điện giờ thấp điểm',
                              evnConfig?.inputOffPeakPrice
                                ? Utils.formatNumber(evnConfig?.inputOffPeakPrice) + ' VNĐ/kWh'
                                : '',
                              null,
                              false
                            )}
                          </>
                        )}
                        {evnConfig?.inputPricingMode === ChargeConstants.ChargePricingMode.TIERED &&
                          Array.isArray(evnConfig?.inputTieredPrice) &&
                          evnConfig.inputTieredPrice.map((input, index, array) => {
                            const isLastTier = index === array.length - 1;
                            const prevUpto = index === 0 ? 0 : array[index - 1]?.upto;
                            const currentUpto = input?.upto;
                            return (
                              <React.Fragment key={`tiered-detail-${index}`}>
                                {UIHelper.buildCardCellItem(
                                  `Giá điện bậc ${index + 1} (Từ ${prevUpto + 1}${isLastTier ? ' kWh trở lên' : `-${currentUpto} kWh`})`,
                                  input?.price ? Utils.formatNumber(input?.price) + ' VNĐ/kWh' : '',
                                  null,
                                  !isLastTier
                                )}
                              </React.Fragment>
                            );
                          })}
                        {/* {UIHelper.buildCardCellItem(
                          'Đối tượng thanh toán',
                          chargeStationDetail?.isAgencyPayEnergyCost
                            ? Utils.isValid(chargeStationDetail?.landlordId)
                              ? 'Chủ mặt bằng'
                              : chargeStationDetail?.agencyId === appConfig?.groupAgencyId
                                ? 'Chủ mặt bằng'
                                : 'NĐT nhượng quyền'
                            : 'Nhà điều hành',
                          null,
                          false
                        )} */}
                      </div>
                    )}
                  </div>

                  {/* mo ta */}
                  {chargeStationDetail?.description && (
                    <div className="mt-4">
                      {UIHelper.buildSectionTitle('Mô tả')}
                      {UIHelper.buildSectionCard(
                        <div
                          className="px-3 py-2"
                          style={UIHelper.buildFontStyle('400', '14px', AppColors.gray[700])}
                        >
                          <ShowMoreText
                            lines={3}
                            more="Xem thêm"
                            less="Thu gọn"
                            className=""
                            anchorClass="text-success font-size-normal font-weight-bolder cursor-pointer hover-opacity-60"
                            onClick={() => { }}
                            expanded={false}
                            truncatedEndingComponent={'... '}
                          >
                            {chargeStationDetail?.description}
                          </ShowMoreText>
                          {/* <span style={UIHelper.buildFontStyle('400', '14px', AppColors.gray[700])}>
                            {chargeStationDetail?.description}
                          </span> */}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {/* right */}
                <div className="col-xl-6 m-0 px-2 mb-4">
                  {/* thong tin lien he */}
                  <div>
                    {UIHelper.buildSectionTitle('Thông tin liên hệ')}
                    {UIHelper.buildSectionCard(
                      <div>
                        {UIHelper.buildCardCellItem('Số điện thoại', chargeStationDetail?.phone)}
                        {UIHelper.buildCardCellItem(
                          'Tên người quản lý trạm sạc',
                          chargeStationDetail?.contactName
                        )}
                        {UIHelper.buildCardCellItem(
                          'Số điện thoại người quản lý',
                          chargeStationDetail?.contactPhone,
                          null,
                          false
                        )}
                      </div>
                    )}
                  </div>
                  {/* dia chi */}
                  <div className='mt-4'>
                    {UIHelper.buildSectionTitle('Địa chỉ')}
                    {UIHelper.buildSectionCard(
                      <div>
                        {UIHelper.buildCardCellItem(
                          'Tỉnh/Thành phố',
                          chargeStationParentArea?.name
                        )}
                        {UIHelper.buildCardCellItem('Phường/Xã', chargeStationArea?.name)}
                        {UIHelper.buildCardCellItem(
                          'Địa chỉ',
                          chargeStationDetail?.address,
                        )}
                        {UIHelper.buildCardCellItem(
                          'Tọa độ',
                          `${chargeStationDetail?.latitude}, ${chargeStationDetail?.longitude}`,
                          null,
                          false
                        )}
                        {/* ban do */}
                        <div className="h-200px" style={{ pointerEvents: 'none' }}>
                          {chargeStationMapEl}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* dich vu di kem */}
                  {chargeStationDetail?.services?.length > 0 && (
                    <div className="mt-4">
                      {UIHelper.buildSectionTitle('Dịch vụ đi kèm')}
                      {UIHelper.buildSectionCard(
                        <div className="px-3 py-2">
                          {_.filter(configServices, (item) =>
                            chargeStationDetail?.services?.includes(item?.serviceCode)
                          )
                            .map((item) => item?.name)
                            .join(', ')}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>,
            true
          )}
        </div>

        {/* hoa hong */}
        {/* <div className="col-lg-4 mb-4 px-2">
          <CommissionCardValue
            name="Hoa hồng tiền điện"
            color="rgba(0, 199, 190, 1)"
            icon={<i className="fa-solid fa-bolt text-white" />}
            children={
              <div>
                <div style={{ borderBottom: `1px dashed ${AppColors.gray[300]}` }}>
                  <div className="px-4 py-3">
                    {UIHelper.buildNameAndValue(
                      'Mô hình chia sẻ của chủ mặt bằng',
                      'Tỷ lệ theo giá điện mua của nhà nước'
                    )}
                  </div>
                  <div className="px-4 pb-3">
                    {UIHelper.buildNameAndValue('Mức hoa hồng chủ mặt bằng', '0%')}
                  </div>
                </div>
                <div>
                  <div className="px-4 py-3">
                    {UIHelper.buildNameAndValue(
                      'Mô hình chia sẻ của NĐT nhượng quyền',
                      'Tỷ lệ theo giá điện mua của nhà nước'
                    )}
                  </div>
                  <div className="px-4 pb-3">
                    {UIHelper.buildNameAndValue('Mức hoa hồng NĐT nhượng quyền', '0%')}
                  </div>
                </div>
              </div>
            }
          />
        </div> */}
      </div>

      {/* danh sach tru sac */}
      <div>
        <div className="px-4">
          <AppTabs tabs={tabs} activeTab={selectedTab} handleClick={handleSelectedTab} />
        </div>
        <AnimatePresence mode="wait">
          <motion.div
            key={selectedTab}
            initial={{ y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -10, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <div className="">
              {selectedTab === tabs[2] && (
                <ChargePointListView
                  stationId={chargeStationId}
                  isTestStation={chargeStationDetail?.isTestStation}
                  hasAppTab={true}
                  enabledEdit={(isAdmin || isInvestor || isLandlord)}
                />
              )}
              {selectedTab === tabs[1] && (
                <div>
                  <ChargeStationEnergyAccountListView
                    energyAccountList={chargeStationDetail?.energyAccounts ?? []}
                    hasAppTab={true}
                    onRefresh={() => {
                      ChargeHelper.API.getChargeStationDetail(chargeStationId).then(
                        (chargeStation) => {
                          if (chargeStation && _.isObject(chargeStation))
                            setChargeStationDetail(chargeStation);
                        }
                      );
                    }}
                  />
                </div>
              )}
              {selectedTab === tabs[0] && (
                <div>
                  <ChargeInventoryListView
                    stationId={chargeStationId}
                    showFullUI={false}
                  />
                </div>
              )}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* bien ban nghiem thu */}
      <div className='px-4 pt-4'>
        {(isAdmin || user?.agency?.primaryRole === AgencyConstants.AgencyRole.INVESTOR) && (
          <div>
            <ChargeStationAcceptanceListView
              acceptanceReportList={chargeStationDetail?.acceptanceReports}
              chargeStationDetail={chargeStationDetail}
              onRefresh={() => {
                ChargeHelper.API.getChargeStationDetail(chargeStationId).then(
                  (chargeStation) => {
                    if (chargeStation && _.isObject(chargeStation))
                      setChargeStationDetail(chargeStation);
                  }
                );
              }}
            />
          </div>
        )}
      </div>

      {/* bieu do thong ke */}
      <div className="mt-4">
        <div className="row m-0 p-0 px-2">
          {/* thong ke dien sac */}
          <div className="col-lg-6 px-2 mb-4">{chartEnergyByDateEl}</div>
          {/* thong ke phien sac */}
          <div className="col-lg-6 px-2 mb-4">{chartTransactionByDateEl}</div>
          {/* thong ke doanh thu */}
          <div className="col-lg-6 px-2 mb-4">{chartRevenueByDateEl}</div>
        </div>
      </div>

      {/* thong bao cua tram */}
      <div className="px-4">
        <ChargeStationLogView chargeStationId={chargeStationId} />
      </div>
    </div>
  );
}

export default ChargeStationDetailView;
